/**
 * Content Moderation Utility
 * Uses AI to check content (text, URLs, images) for inappropriate material
 */

import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Moderate text content for inappropriate material
 * @param {string} content - Text content to check
 * @returns {Promise<{is_safe: boolean, message: string, categories_flagged: string[]}>}
 */
export const moderateText = async (content) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${API_URL}/ai/moderate-content`,
      { content_type: 'text', content },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Moderation error:', error);
    // Default to safe if moderation fails
    return { is_safe: true, message: 'Moderation skipped', categories_flagged: [] };
  }
};

/**
 * Moderate a URL (image or video) for inappropriate material
 * @param {string} url - URL to check
 * @param {'image_url' | 'video_url'} type - Type of URL
 * @returns {Promise<{is_safe: boolean, message: string, categories_flagged: string[]}>}
 */
export const moderateUrl = async (url, type = 'image_url') => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${API_URL}/ai/moderate-content`,
      { content_type: type, content: url },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error('URL moderation error:', error);
    return { is_safe: true, message: 'Moderation skipped', categories_flagged: [] };
  }
};

/**
 * Moderate an uploaded image file
 * @param {File} file - Image file to check
 * @returns {Promise<{is_safe: boolean, message: string, categories_flagged: string[]}>}
 */
export const moderateImage = async (file) => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(
      `${API_URL}/ai/moderate-image`,
      formData,
      { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        } 
      }
    );
    return response.data;
  } catch (error) {
    console.error('Image moderation error:', error);
    return { is_safe: true, message: 'Moderation skipped', categories_flagged: [] };
  }
};

/**
 * Check if a URL looks suspicious (basic pattern matching)
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isSuspiciousUrl = (url) => {
  if (!url) return false;
  
  const suspiciousPatterns = [
    /adult/i,
    /xxx/i,
    /porn/i,
    /nsfw/i,
    /explicit/i,
    /nude/i,
    /sex/i,
  ];
  
  return suspiciousPatterns.some(pattern => pattern.test(url));
};

export default {
  moderateText,
  moderateUrl,
  moderateImage,
  isSuspiciousUrl,
};
